import { FC, useEffect, useMemo, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';

import { Chip, Icon, IconButton, Popover, PopoverItem } from '@myn/mui';

import { ROW_STATUSES } from '../../../constants/campaignBuilder.js';
import { useSetStatusOfRows } from '../../../hooks/useStatusOfRows.js';
import { Option } from '../../../types/campaignBuilder.js';

type Props = {
  variant?: 'default' | 'icon';
};

const RowsSelect: FC<Props> = ({ variant = 'default' }) => {
  const { t } = useTranslation('@myn/planning');
  const setStatusOfRows = useSetStatusOfRows();

  const statusOptions = useMemo(
    () => [
      {
        value: ROW_STATUSES.ALL,
        name: t('All rows'),
      },
      {
        value: ROW_STATUSES.PLANNED,
        name: t('Planned rows'),
      },
      {
        value: ROW_STATUSES.UNPLANNED,
        name: t('Unplanned rows'),
      },
    ],
    [t],
  );

  const [option, setOption] = useState<Option<string>>(statusOptions[0]);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handlePopoverOpen = (
    event: React.MouseEvent<HTMLDivElement | HTMLButtonElement>,
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleChangeValue = (opt: Option<ROW_STATUSES>) => {
    setOption(opt);
    setStatusOfRows(opt.value);
    handlePopoverClose();
  };

  // it is a fix of issue with language change, when the component does not rerender list of options and selected option
  useEffect(() => {
    if (!statusOptions.some((item) => item.name === option.name)) {
      const item =
        statusOptions.find((o) => o.value === option.value) || statusOptions[0];
      setOption(item);
    }
  }, [t]);

  return (
    <>
      {variant === 'icon' ? (
        <IconButton
          aria-label={anchorEl ? 'Hide row filter' : 'Show row filter'}
          variant="outlined"
          size="medium"
          color="primary"
          icon="Filter"
          onClick={handlePopoverOpen}
          data-testid="row-select-chip"
        />
      ) : (
        <Chip
          aria-label={anchorEl ? 'Hide row filter' : 'Show row filter'}
          variant="filled"
          size="medium"
          color="tertiary"
          icon={<Icon component="Filter" />}
          label={option.name}
          onClick={handlePopoverOpen}
        />
      )}

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: variant === 'icon' ? 'right' : 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: variant === 'icon' ? 'right' : 'left',
        }}
      >
        {statusOptions.map((item) => (
          <PopoverItem
            key={item.value}
            text={item.name}
            selected={item.value === option.value}
            onClick={() => handleChangeValue(item)}
            sx={{ width: '217px', height: '40px' }}
          />
        ))}
      </Popover>
    </>
  );
};

export default RowsSelect;
